import type { PreTriageEvaluationResult, PreTriageExternalOutcome } from '@core/entities/PreTriage';
import type {
  TriageQuestion,
  TriageQuestionnaire,
  TriageQuestionType,
  TriageNumericFormat,
} from '@core/entities/Triage';
import { PreTriageGatewayError } from '@core/ports/PreTriageGateway';
import { reconnectFromNhsAppGuidancePoints, restartFromNhsAppGuidancePoints, safeGuidanceHeading } from './safeGuidance';

export interface TriageChoiceOptionViewModel {
  code: string;
  label: string;
  hint?: string;
}

export interface TriageQuestionViewModel {
  questionKey?: string;
  linkId: string;
  legend: string;
  type: TriageQuestionType;
  required: boolean;
  options?: readonly TriageChoiceOptionViewModel[];
  children?: readonly TriageQuestionViewModel[];
  allowsMultiple?: boolean;
  numericFormat?: TriageNumericFormat;
  hint?: string;
  min?: number;
  max?: number;
  sourceListUrl?: string;
  sliderTitle?: string;
}

export interface PreTriageReadyPageViewModel {
  kind: 'ready';
  title: string;
  intro: string;
  questions: readonly TriageQuestionViewModel[];
}

export interface PreTriageFailurePageViewModel {
  kind: 'failure';
  guidanceHeading: string;
  guidancePoints: string[];
  message: string;
  title: string;
}

export type PreTriagePageViewModel = PreTriageReadyPageViewModel | PreTriageFailurePageViewModel;

export interface TriageOutcomeViewModel {
  outcome: PreTriageEvaluationResult['outcome'];
  title: string;
  message: string;
  externalOutcome?: PreTriageExternalOutcome;
  redirectPath?: string;
  conditions?: PreTriageEvaluationResult['conditions'];
  summaryItems?: PreTriageEvaluationResult['summaryItems'];
  details?: PreTriageEvaluationResult['details'];
  doItems?: PreTriageEvaluationResult['doItems'];
  dontItems?: PreTriageEvaluationResult['dontItems'];
  tasks?: PreTriageEvaluationResult['tasks'];
}

export interface PreTriageOutcomePageViewModel {
  kind: 'outcome';
  outcome: TriageOutcomeViewModel;
}

export type PreTriageLoadResultViewModel = PreTriagePageViewModel | PreTriageOutcomePageViewModel;

export type PreTriageSubmissionResultViewModel =
  | { kind: 'questionnaire'; page: PreTriageReadyPageViewModel }
  | { kind: 'outcome'; outcome: TriageOutcomeViewModel }
  | { kind: 'failure'; failure: PreTriageFailurePageViewModel };

export class PreTriagePresenter {
  public presentQuestionnaire(questionnaire: TriageQuestionnaire): PreTriageReadyPageViewModel {
    const workflowStepIdentity = questionnaire.workflowStepIdentity ?? {
      journeyRevision: 1,
      stepSequence: 1,
      questionnaireEventSequence: 1,
    };
    const questionIdentity = [
      questionnaire.id,
      workflowStepIdentity.journeyRevision,
      workflowStepIdentity.stepSequence,
      workflowStepIdentity.questionnaireEventSequence,
    ].join(':');

    return {
      kind: 'ready',
      title: questionnaire.title,
      intro: 'Answer these questions so we can guide you through triage safely.',
      questions: questionnaire.questions.map((question, index) =>
        this.presentQuestion(question, questionIdentity, [index])),
    };
  }

  private presentQuestion(
    question: TriageQuestion,
    questionIdentity: string,
    questionPath: readonly number[],
  ): TriageQuestionViewModel {
    return {
      questionKey: `${questionIdentity}:${questionPath.join('.')}:${question.linkId}`,
      linkId: question.linkId,
      legend: question.text,
      type: question.type,
      required: question.required,
      hint: question.metadata?.explanatoryText,
      allowsMultiple: question.allowsMultiple,
      numericFormat: question.numericFormat,
      min: question.metadata?.minimumValue,
      max: question.metadata?.maximumValue,
      sourceListUrl: question.metadata?.sourceListUrl,
      sliderTitle: question.metadata?.sliderTitle,
      options: question.options?.map((option) => ({
        code: option.code,
        label: option.label,
        hint: option.hint,
      })),
      children: question.children?.map((child, index) =>
        this.presentQuestion(child, questionIdentity, [...questionPath, index])),
    };
  }

  public presentOutcome(result: PreTriageEvaluationResult): TriageOutcomeViewModel {
    return {
      outcome: result.outcome,
      title: result.title,
      message: result.message,
      ...(result.externalOutcome ? { externalOutcome: result.externalOutcome } : {}),
      redirectPath: result.redirectPath,
      ...(result.conditions && result.conditions.length > 0 ? { conditions: result.conditions } : {}),
      ...(result.summaryItems && result.summaryItems.length > 0 ? { summaryItems: result.summaryItems } : {}),
      ...(result.details && result.details.length > 0 ? { details: result.details } : {}),
      ...(result.doItems && result.doItems.length > 0 ? { doItems: result.doItems } : {}),
      ...(result.dontItems && result.dontItems.length > 0 ? { dontItems: result.dontItems } : {}),
      ...(result.tasks && result.tasks.length > 0 ? { tasks: result.tasks } : {}),
    };
  }

  public presentFailure(error: unknown): PreTriageFailurePageViewModel {
    if (error instanceof PreTriageGatewayError && error.code === 'offline') {
      return {
        kind: 'failure',
        title: 'Check your internet connection',
        message: 'The next Intelligent Navigator step cannot load because your internet connection appears to be unavailable.',
        guidanceHeading: safeGuidanceHeading,
        guidancePoints: [...reconnectFromNhsAppGuidancePoints],
      };
    }

    if (error instanceof PreTriageGatewayError && error.code === 'session-required') {
      return {
        kind: 'failure',
        title: 'Start again from the NHS App',
        message: 'We could not confirm your NHS App session, so Intelligent Navigator cannot continue safely from this page.',
        guidanceHeading: safeGuidanceHeading,
        guidancePoints: [...restartFromNhsAppGuidancePoints],
      };
    }

    if (error instanceof PreTriageGatewayError && error.code === 'retry-exhausted') {
      return {
        kind: 'failure',
        title: 'Intelligent Navigator cannot continue',
        message: 'There is a problem loading the information needed to continue this journey right now.',
        guidanceHeading: safeGuidanceHeading,
        guidancePoints: [...reconnectFromNhsAppGuidancePoints],
      };
    }

    return {
      kind: 'failure',
      title: 'Intelligent Navigator cannot continue',
      message: 'There is a problem loading the information needed to continue this journey right now.',
      guidanceHeading: safeGuidanceHeading,
      guidancePoints: [...reconnectFromNhsAppGuidancePoints],
    };
  }

  public presentNextQuestionnaire(questionnaire: TriageQuestionnaire): PreTriageSubmissionResultViewModel {
    return {
      kind: 'questionnaire',
      page: this.presentQuestionnaire(questionnaire),
    };
  }

  public presentLoadOutcome(result: PreTriageEvaluationResult): PreTriageOutcomePageViewModel {
    return {
      kind: 'outcome',
      outcome: this.presentOutcome(result),
    };
  }

  public presentSubmissionOutcome(result: PreTriageEvaluationResult): PreTriageSubmissionResultViewModel {
    return this.presentLoadOutcome(result);
  }

  public presentSubmissionFailure(error: unknown): PreTriageSubmissionResultViewModel {
    return {
      kind: 'failure',
      failure: this.presentFailure(error),
    };
  }
}